import { useTranslation } from 'react-i18next';
import {
  DialogClose,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
  toast,
} from '@qodo/design-system';
import { useDeleteTeamMutation } from '../../network/mutations';
import {
  sanitizeError,
  showApiErrorToast,
} from '../../network/mutations/errorToast';
import { renderTranslations, spawn } from '../../utils/translations';
import { DeleteTeamDialogProps } from './types';

export const DeleteTeamDialog = (props: DeleteTeamDialogProps) => {
  const { team, onOpenChange = () => {} } = props;
  const { t } = useTranslation();
  const spawnModalContent = spawn(`dialogs.delete-team`);
  const spawnModalActions = spawn(`dialogs.delete-team.action`);
  const { mutateAsync: deleteTeamMutateAsync, isPending: isDeleteTeamPending } =
    useDeleteTeamMutation();

  const onSubmit = async () => {
    try {
      await deleteTeamMutateAsync(team.id);
      toast.success(t('toasts.teams.delete-team.success'));
      onOpenChange(false);
    } catch (error: unknown) {
      console.error('Failed to delete team:', sanitizeError(error));
      showApiErrorToast(error, t('toasts.teams.delete-team.error'));
    }
  };

  return (
    <Dialog {...props}>
      <DialogContent autoFocus={false} className="sm:max-w-85 md:max-w-[425px]">
        <DialogHeader>
          <DialogTitle
            title={team.name}
            className="text-h3 sm:max-w-55 md:max-w-[330px] overflow-hidden text-ellipsis whitespace-nowrap cursor-default"
          >
            {spawnModalContent('title', { name: team.name })}
          </DialogTitle>
        </DialogHeader>
        <DialogDescription className="flex flex-col gap-4">
          {renderTranslations(spawnModalContent('description'))}
        </DialogDescription>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" disabled={isDeleteTeamPending}>
              {spawnModalActions('cancel')}
            </Button>
          </DialogClose>
          <Button
            variant="danger"
            onClick={onSubmit}
            loading={isDeleteTeamPending}
          >
            {spawnModalActions('delete')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
